import {
  addDoc, collection, deleteDoc, deleteField, doc, getDoc, limit, onSnapshot,
  orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { firestore } from '../views/firebaseConfig';
import {
  COLLECTIONS, TEAM_JOIN_POLICY, TEAM_MEMBER_STATUS, TEAM_ROLE, TEAM_STATUS, list, normalizeStatus, number, text,
} from '../contracts/p4r';

const teamRef = (teamId) => doc(firestore, COLLECTIONS.TEAMS, teamId);
const memberRef = (teamId, uid) => doc(firestore, COLLECTIONS.TEAMS, teamId, 'members', uid);
const requestRef = (teamId, uid) => doc(firestore, COLLECTIONS.TEAMS, teamId, 'requests', uid);
const banRef = (teamId, uid) => doc(firestore, COLLECTIONS.TEAMS, teamId, 'bans', uid);

export const mapTeam = (snapshot) => {
  const data = snapshot.data();
  const visibility = normalizeStatus(data.visibility, 'PUBLIC');
  return {
    id: snapshot.id,
    ...data,
    name: text(data.name),
    bio: text(data.bio),
    visibility,
    joinPolicy: normalizeStatus(data.joinPolicy, visibility === 'PRIVATE' ? 'APPROVAL_REQUIRED' : 'OPEN'),
    status: normalizeStatus(data.status, TEAM_STATUS.ACTIVE),
    formats: list(data.formats),
    admins: list(data.admins),
    membersCount: number(data.membersCount),
    primaryVenue: text(data.primaryVenue || data.homeLabel),
  };
};

export function listenPublicTeams(onData, onError) {
  const q = query(
    collection(firestore, COLLECTIONS.TEAMS),
    where('visibility', '==', 'PUBLIC'),
    where('status', '==', TEAM_STATUS.ACTIVE),
    limit(100)
  );
  return onSnapshot(q, (snapshot) => {
    const teams = snapshot.docs.map(mapTeam)
      .filter((team) => team.visibility === 'PUBLIC')
      .sort((a, b) => (b.timeUpdated?.seconds || 0) - (a.timeUpdated?.seconds || 0));
    onData(teams);
  }, onError);
}

export async function getTeam(teamId) {
  const snapshot = await getDoc(teamRef(teamId));
  return snapshot.exists() ? mapTeam(snapshot) : null;
}

export function listenTeam(teamId, onData, onError) {
  return onSnapshot(teamRef(teamId), (snapshot) => onData(snapshot.exists() ? mapTeam(snapshot) : null), onError);
}

export function listenTeamMembers(teamId, onData, onError) {
  return onSnapshot(collection(firestore, COLLECTIONS.TEAMS, teamId, 'members'), (snapshot) => {
    onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), status: normalizeStatus(item.data().status), role: normalizeStatus(item.data().role) })));
  }, onError);
}

export function listenTeamRequests(teamId, onData, onError) {
  return onSnapshot(collection(firestore, COLLECTIONS.TEAMS, teamId, 'requests'), (snapshot) => {
    onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), status: normalizeStatus(item.data().status) })));
  }, onError);
}

export function listenTeamBoard(teamId, onData, onError) {
  const q = query(collection(firestore, COLLECTIONS.TEAMS, teamId, 'board'), orderBy('timeCreated', 'desc'), limit(50));
  return onSnapshot(q, (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError);
}

export async function createTeam(uid, profile, form) {
  const ref = doc(collection(firestore, COLLECTIONS.TEAMS));
  const now = serverTimestamp();
  const payload = {
    teamId: ref.id,
    name: text(form.name),
    bio: text(form.bio),
    ownerId: uid,
    admins: [uid],
    membersCount: 1,
    visibility: normalizeStatus(form.visibility, 'PUBLIC'),
    joinPolicy: normalizeStatus(form.joinPolicy, TEAM_JOIN_POLICY.OPEN),
    status: TEAM_STATUS.ACTIVE,
    formats: list(form.formats),
    primaryVenue: text(form.primaryVenue),
    displayHome: text(form.primaryVenue || form.homeLabel),
    homeLabel: text(form.homeLabel || form.primaryVenue),
    homeCity: text(form.homeCity),
    homeState: text(form.homeState),
    homeZip: text(form.homeZip),
    homeCountry: text(form.homeCountry || 'US'),
    ...(form.minRating === '' || form.minRating == null ? {} : { minRating: number(form.minRating) }),
    bannerUrl: text(form.bannerUrl),
    ownerName: text(profile?.nickname || profile?.name),
    timeCreated: now,
    timeUpdated: now,
  };
  const batch = writeBatch(firestore);
  batch.set(ref, payload);
  batch.set(memberRef(ref.id, uid), {
    uid, role: TEAM_ROLE.OWNER, status: TEAM_MEMBER_STATUS.ACTIVE,
    timeJoined: now, timeUpdated: now, lastActionType: 'CREATE_TEAM', lastActionBy: uid, lastActionByUid: uid,
  });
  await batch.commit();
  return ref.id;
}

export async function updateTeam(teamId, form) {
  await updateDoc(teamRef(teamId), {
    name: text(form.name), bio: text(form.bio), visibility: normalizeStatus(form.visibility, 'PUBLIC'),
    joinPolicy: normalizeStatus(form.joinPolicy, TEAM_JOIN_POLICY.OPEN), formats: list(form.formats),
    primaryVenue: text(form.primaryVenue), displayHome: text(form.primaryVenue || form.homeLabel),
    homeLabel: text(form.homeLabel || form.primaryVenue), homeCity: text(form.homeCity), homeState: text(form.homeState),
    homeZip: text(form.homeZip), homeCountry: text(form.homeCountry || 'US'),
    minRating: form.minRating === '' || form.minRating == null ? deleteField() : number(form.minRating),
    bannerUrl: text(form.bannerUrl), timeUpdated: serverTimestamp(),
  });
}

export async function joinOrRequestTeam(teamId, uid) {
  const [teamSnapshot, memberSnapshot, banSnapshot] = await Promise.all([
    getDoc(teamRef(teamId)), getDoc(memberRef(teamId, uid)), getDoc(banRef(teamId, uid)),
  ]);
  if (!teamSnapshot.exists()) throw new Error('This team is no longer available.');
  if (banSnapshot.exists()) throw new Error('You cannot join this team.');
  const team = mapTeam(teamSnapshot);
  if (memberSnapshot.exists() && normalizeStatus(memberSnapshot.data().status) === TEAM_MEMBER_STATUS.ACTIVE) return 'ALREADY_MEMBER';
  if (team.joinPolicy === TEAM_JOIN_POLICY.APPROVAL_REQUIRED || team.visibility === 'PRIVATE') {
    await setDoc(requestRef(teamId, uid), { uid, status: 'PENDING', timeRequested: serverTimestamp(), timeUpdated: serverTimestamp() });
    return 'REQUESTED';
  }
  await runTransaction(firestore, async (transaction) => {
    const latestTeam = await transaction.get(teamRef(teamId));
    const latestMember = await transaction.get(memberRef(teamId, uid));
    if (!latestTeam.exists()) throw new Error('This team is no longer available.');
    const wasActive = latestMember.exists() && normalizeStatus(latestMember.data().status) === TEAM_MEMBER_STATUS.ACTIVE;
    if (wasActive) return;
    transaction.set(memberRef(teamId, uid), {
      uid, role: latestMember.exists() ? normalizeStatus(latestMember.data().role, TEAM_ROLE.MEMBER) : TEAM_ROLE.MEMBER,
      status: TEAM_MEMBER_STATUS.ACTIVE, timeJoined: serverTimestamp(), timeUpdated: serverTimestamp(),
      lastActionType: 'JOIN_TEAM', lastActionBy: uid, lastActionByUid: uid,
    });
    transaction.update(teamRef(teamId), { membersCount: number(latestTeam.data().membersCount) + 1 });
  });
  return 'JOINED';
}

export async function cancelJoinRequest(teamId, uid) { await deleteDoc(requestRef(teamId, uid)); }

export async function leaveTeam(teamId, uid) {
  await runTransaction(firestore, async (transaction) => {
    const [teamSnapshot, memberSnapshot] = await Promise.all([transaction.get(teamRef(teamId)), transaction.get(memberRef(teamId, uid))]);
    if (!teamSnapshot.exists() || !memberSnapshot.exists()) throw new Error('Membership not found.');
    if (normalizeStatus(memberSnapshot.data().role) === TEAM_ROLE.OWNER) throw new Error('Transfer ownership or delete the team before leaving.');
    if (normalizeStatus(memberSnapshot.data().status) !== TEAM_MEMBER_STATUS.ACTIVE) return;
    transaction.update(memberRef(teamId, uid), {
      status: TEAM_MEMBER_STATUS.LEFT, timeUpdated: serverTimestamp(), lastActionType: 'LEAVE_TEAM', lastActionBy: uid, lastActionByUid: uid,
    });
    transaction.update(teamRef(teamId), { membersCount: Math.max(0, number(teamSnapshot.data().membersCount) - 1) });
  });
}

async function requireModerator(teamId, actorUid) {
  const team = await getTeam(teamId);
  if (!team || (team.ownerId !== actorUid && !team.admins.includes(actorUid))) throw new Error('Team moderator access required.');
  return team;
}

export async function approveRequest(teamId, targetUid, actorUid) {
  await requireModerator(teamId, actorUid);
  await runTransaction(firestore, async (transaction) => {
    const [teamSnapshot, requestSnapshot, memberSnapshot] = await Promise.all([
      transaction.get(teamRef(teamId)), transaction.get(requestRef(teamId, targetUid)), transaction.get(memberRef(teamId, targetUid)),
    ]);
    if (!requestSnapshot.exists()) throw new Error('This request is no longer pending.');
    const alreadyActive = memberSnapshot.exists() && normalizeStatus(memberSnapshot.data().status) === TEAM_MEMBER_STATUS.ACTIVE;
    transaction.set(memberRef(teamId, targetUid), {
      uid: targetUid, role: TEAM_ROLE.MEMBER, status: TEAM_MEMBER_STATUS.ACTIVE,
      timeJoined: serverTimestamp(), timeUpdated: serverTimestamp(), lastActionType: 'APPROVE_REQUEST', lastActionBy: actorUid, lastActionByUid: actorUid,
    });
    transaction.delete(requestRef(teamId, targetUid));
    if (!alreadyActive) transaction.update(teamRef(teamId), { membersCount: number(teamSnapshot.data().membersCount) + 1 });
  });
}

export async function denyRequest(teamId, targetUid, actorUid) {
  await requireModerator(teamId, actorUid);
  await deleteDoc(requestRef(teamId, targetUid));
}

export async function setMemberRole(teamId, targetUid, role, actorUid) {
  const team = await requireModerator(teamId, actorUid);
  if (team.ownerId !== actorUid) throw new Error('Only the team owner can change admin roles.');
  if (targetUid === team.ownerId) throw new Error('The owner role cannot be changed here.');
  await updateDoc(memberRef(teamId, targetUid), {
    role: normalizeStatus(role, TEAM_ROLE.MEMBER), status: TEAM_MEMBER_STATUS.ACTIVE,
    timeUpdated: serverTimestamp(), lastActionType: role === TEAM_ROLE.ADMIN ? 'PROMOTE_ADMIN' : 'DEMOTE_ADMIN',
    lastActionBy: actorUid, lastActionByUid: actorUid,
  });
  const nextAdmins = role === TEAM_ROLE.ADMIN ? Array.from(new Set([...team.admins, targetUid])) : team.admins.filter((uid) => uid !== targetUid);
  await updateDoc(teamRef(teamId), { admins: nextAdmins, timeUpdated: serverTimestamp() });
}

export async function removeMember(teamId, targetUid, actorUid, ban = false, reason = '') {
  const team = await requireModerator(teamId, actorUid);
  if (targetUid === team.ownerId) throw new Error('The team owner cannot be removed.');
  await runTransaction(firestore, async (transaction) => {
    const [teamSnapshot, memberSnapshot] = await Promise.all([transaction.get(teamRef(teamId)), transaction.get(memberRef(teamId, targetUid))]);
    const wasActive = memberSnapshot.exists() && normalizeStatus(memberSnapshot.data().status) === TEAM_MEMBER_STATUS.ACTIVE;
    transaction.set(memberRef(teamId, targetUid), {
      uid: targetUid, role: memberSnapshot.exists() ? normalizeStatus(memberSnapshot.data().role, TEAM_ROLE.MEMBER) : TEAM_ROLE.MEMBER,
      status: ban ? TEAM_MEMBER_STATUS.BANNED : TEAM_MEMBER_STATUS.LEFT,
      timeJoined: memberSnapshot.data()?.timeJoined || serverTimestamp(), timeUpdated: serverTimestamp(),
      lastActionType: ban ? 'BAN_MEMBER' : 'REMOVE_MEMBER', lastActionBy: actorUid, lastActionByUid: actorUid,
    });
    if (ban) transaction.set(banRef(teamId, targetUid), { uid: targetUid, reason: text(reason), bannedBy: actorUid, timeCreated: serverTimestamp() });
    if (wasActive) transaction.update(teamRef(teamId), { membersCount: Math.max(0, number(teamSnapshot.data().membersCount) - 1) });
  });
}

export async function createBoardPost(teamId, actorUid, actorName, body, category = '') {
  await requireModerator(teamId, actorUid);
  return addDoc(collection(firestore, COLLECTIONS.TEAMS, teamId, 'board'), {
    body: text(body), category: text(category), createdBy: actorUid, createdByName: text(actorName),
    pinned: false, timeCreated: serverTimestamp(), timeUpdated: serverTimestamp(),
  });
}

export async function archiveTeam(teamId) { await updateDoc(teamRef(teamId), { status: TEAM_STATUS.DELETED, timeUpdated: serverTimestamp() }); }

export async function getProfilesByIds(uids) {
  const unique = Array.from(new Set(uids.filter(Boolean)));
  const snapshots = await Promise.all(unique.map((uid) => getDoc(doc(firestore, COLLECTIONS.USER_PROFILES, uid))));
  return Object.fromEntries(snapshots.filter((item) => item.exists()).map((item) => [item.id, item.data()]));
}
